import httpService from "./HttpService";
import {
    adminRecipesControllerGetRecipes,
    adminRecipesControllerGetRecipe,
    adminRecipesControllerDeleteRecipe,
} from "../api/generated";

// Attaches nutrition fields to the request metadata when present. Macros
// (protein/carbs/fat/fiber) travel inside each measurement unit; the list is sent as-is
// (the server drops blank-name rows).
const appendNutrition = (metaData, recipeData) => {
    if (recipeData.measurementUnits !== undefined) {
        metaData.measurementUnits = recipeData.measurementUnits || [];
    }
};

// Migrated to the generated client. The endpoint's query is FLAT (pageId/pageSize/keyword/
// categoryId/tagIds — no nested Spring `pageable`), so it serializes correctly through the SDK.
// The server no longer accepts a `companyId` query param (company is derived from the authed
// principal — the generated query type has no companyId), so the old `companyId` arg is
// accepted for signature parity but not forwarded. Returns {data: PageRecipeDto} so callers
// keep reading `res.data.content/totalElements/totalPages` unchanged.
export const getRecipes = async (pageId = 0, pageSize = 20, keyword, companyId, categoryId, tagIds) => {
    const {data, error} = await adminRecipesControllerGetRecipes({
        query: {
            pageId,
            pageSize,
            ...(keyword ? {keyword} : {}),
            ...(categoryId ? {categoryId} : {}),
            ...(tagIds?.length ? {tagIds} : {}),
        },
    });
    if (error) throw error;
    return {data};
};

// Migrated. Returns {data: RecipeDto}; callers read `res.data`.
export const getRecipeById = async (id) => {
    const {data, error} = await adminRecipesControllerGetRecipe({path: {id}});
    if (error) throw error;
    return {data};
};

// TODO(openapi-migration): multipart/file upload (pdf/video/thumbnail + JSON metaData part).
// The generated SDK models the body as {pdf, video, thumbnail, metaData: string}, but this
// stays on httpService/FormData so the axios error shape (error.response.data.errorMessage)
// the create/edit dialog reads is preserved.
export const saveRecipe = async (recipeData) => {
    const token = localStorage.getItem("auth");

    const formData = new FormData();

    if (recipeData.pdfFile) {
        formData.append("pdf", recipeData.pdfFile);
    }

    if (recipeData.video) {
        formData.append("video", recipeData.video);
    }

    if (recipeData.thumbnail) {
        formData.append("thumbnail", recipeData.thumbnail);
    }

    const metaData = {
        title: recipeData.title,
        description: recipeData.description,
        companyId: recipeData.companyId || null,
        pdfFileName: recipeData.pdfFileName,
        categoryId: recipeData.categoryId || null,
        tagIds: recipeData.tagIds || [],
        contentType: recipeData.contentType,
        // Optional external website link. Empty string clears it server-side.
        linkUrl: recipeData.linkUrl || null
    };

    if (recipeData.content !== undefined) {
        metaData.content = recipeData.content || null;
    }

    appendNutrition(metaData, recipeData);

    // Only send videoUrl OR videoFileName, not both
    if (recipeData.videoFilename) {
        metaData.videoFileName = recipeData.videoFilename;
    } else if (recipeData.videoUrl !== undefined) {
        metaData.videoUrl = recipeData.videoUrl || null;
    }

    // Send thumbnailUrl for YouTube videos
    if (recipeData.thumbnailUrl) {
        metaData.thumbnailUrl = recipeData.thumbnailUrl;
    }

    formData.append("metaData", JSON.stringify(metaData));

    return await httpService.post("admin/recipes", formData, token);
};

// TODO(openapi-migration): multipart/file upload (pdf/video/thumbnail + JSON metaData part).
// Stays on httpService/FormData; see saveRecipe.
export const updateRecipe = async (id, recipeData) => {
    const token = localStorage.getItem("auth");

    const formData = new FormData();

    if (recipeData.pdfFile) {
        formData.append("pdf", recipeData.pdfFile);
    }

    if (recipeData.video) {
        formData.append("video", recipeData.video);
    }

    if (recipeData.thumbnail) {
        formData.append("thumbnail", recipeData.thumbnail);
    }

    const metaData = {};

    if (recipeData.title !== undefined) {
        metaData.title = recipeData.title;
    }

    if (recipeData.description !== undefined) {
        metaData.description = recipeData.description;
    }

    if (recipeData.videoUrl !== undefined) {
        metaData.videoUrl = recipeData.videoUrl || null;
    }

    if (recipeData.videoFilename !== undefined) {
        metaData.videoFileName = recipeData.videoFilename;
    }

    // Send thumbnailUrl for YouTube videos
    if (recipeData.thumbnailUrl) {
        metaData.thumbnailUrl = recipeData.thumbnailUrl;
    }

    if (recipeData.removeVideo) {
        metaData.removeVideo = true;
    }

    if (recipeData.removeThumbnail) {
        metaData.removeThumbnail = true;
    }

    if (recipeData.categoryId !== undefined) {
        metaData.categoryId = recipeData.categoryId;
    }

    if (recipeData.tagIds !== undefined) {
        metaData.tagIds = recipeData.tagIds;
    }

    if (recipeData.pdfFileName !== undefined) {
        metaData.pdfFileName = recipeData.pdfFileName;
    }

    if (recipeData.contentType !== undefined) {
        metaData.contentType = recipeData.contentType;
    }

    if (recipeData.content !== undefined) {
        metaData.content = recipeData.content;
    }

    if (recipeData.removePdf) {
        metaData.removePdf = true;
    }

    // Sent verbatim (no `|| null` coalescing): "" is the explicit clear signal the
    // server maps to null, while omitting the key leaves the existing link untouched.
    if (recipeData.linkUrl !== undefined) {
        metaData.linkUrl = recipeData.linkUrl;
    }

    appendNutrition(metaData, recipeData);

    formData.append("metaData", JSON.stringify(metaData));

    return await httpService.patch(`admin/recipes/${id}`, formData, token);
};

// Migrated. Old code returned the raw axios response; the delete result is only awaited
// (no field read), so we return {data} for parity with the other migrated getters.
export const deleteRecipe = async (id) => {
    const {data, error} = await adminRecipesControllerDeleteRecipe({path: {id}});
    if (error) throw error;
    return {data};
};
